import type { KeyboardEvent } from 'react';

export type PortfolioHealth = 'healthy' | 'at-risk' | 'needs-attention';

/** One tile of the Executive treemap. Recharts clones it per node and passes the node's data as props. */
export interface PortfolioTileProps {
  onOpen: (companyId: string) => void;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  depth?: number;
  name?: string;
  health?: PortfolioHealth;
  progress?: number;
  blocked?: number;
  items?: number;
  companyId?: string;
}

// The health shows as a light tint and the progress bar; a tile is never filled with the full color.
const TONES: Record<PortfolioHealth, { label: string; tile: string; solid: string; track: string }> = {
  healthy: {
    label: 'On track',
    tile: 'fill-success/10 stroke-success/30 group-hover:fill-success/20 group-focus-visible:stroke-success',
    solid: 'fill-success',
    track: 'fill-success/20',
  },
  'needs-attention': {
    label: 'Needs attention',
    tile: 'fill-warning/10 stroke-warning/30 group-hover:fill-warning/20 group-focus-visible:stroke-warning',
    solid: 'fill-warning',
    track: 'fill-warning/20',
  },
  'at-risk': {
    label: 'At risk',
    tile: 'fill-destructive/10 stroke-destructive/30 group-hover:fill-destructive/20 group-focus-visible:stroke-destructive',
    solid: 'fill-destructive',
    track: 'fill-destructive/20',
  },
};

const GAP = 4;
const PADDING = 12;
const NAME_CHAR_WIDTH = 7.2;
const SMALL_CHAR_WIDTH = 6;

function fit(text: string, room: number, charWidth: number): string {
  const max = Math.floor(room / charWidth);
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

export function PortfolioTile({ onOpen, x = 0, y = 0, width = 0, height = 0, depth, name = '', health, progress = 0, blocked = 0, items = 0, companyId }: PortfolioTileProps) {
  // Recharts also hands over the root node, which spans the whole chart and would tint behind every tile.
  if (depth === 0 || !health || !companyId) return null;

  const tone = TONES[health];
  const w = width - GAP * 2;
  const h = height - GAP * 2;
  if (w <= 0 || h <= 0) return null;

  const left = x + GAP + PADDING;
  const top = y + GAP;
  const room = w - PADDING * 2;
  const showName = w >= 70 && h >= 62;
  const showPercent = w >= 44 && h >= 34;
  const showStatus = w >= 130 && h >= 96;
  const showBar = room >= 40 && h >= 78;
  const showItems = w >= 130 && h >= 124;
  const showBlockedPill = blocked > 0 && w >= 190;
  const showBlockedMark = blocked > 0 && !showBlockedPill && w >= 70 && h >= 62;
  const blockedText = `${blocked} blocked`;
  const pillWidth = blockedText.length * SMALL_CHAR_WIDTH + 14;
  const nameRoom = room - (showBlockedPill ? pillWidth + 8 : showBlockedMark ? 18 : 0);
  const percentY = showName ? top + 56 : top + h / 2 + 7;
  const barY = top + h - PADDING - 4;

  const open = () => onOpen(companyId);
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  };

  return (
    <g
      className="group cursor-pointer outline-none"
      role="button"
      tabIndex={0}
      aria-label={`Open ${name}: ${tone.label}, ${progress}% complete${blocked > 0 ? `, ${blockedText}` : ''}`}
      onClick={open}
      onKeyDown={onKeyDown}
    >
      <rect x={x + GAP} y={y + GAP} width={w} height={h} rx={10} ry={10} strokeWidth={1} className={`${tone.tile} transition-colors`} />

      {showName && (
        <text x={left} y={top + 25} fontSize={13} fontWeight={600} className="fill-foreground pointer-events-none">
          {fit(name, nameRoom, NAME_CHAR_WIDTH)}
        </text>
      )}

      {showPercent && (
        <text x={showName ? left : x + GAP + w / 2} y={percentY} textAnchor={showName ? 'start' : 'middle'} fontSize={showName ? 22 : 14} fontWeight={600} className="fill-foreground pointer-events-none">
          {progress}%
        </text>
      )}

      {showStatus && (
        <g className="pointer-events-none">
          <circle cx={left + 3.5} cy={top + 73} r={3.5} className={tone.solid} />
          <text x={left + 13} y={top + 77} fontSize={11} className="fill-muted-foreground">
            {tone.label}
          </text>
        </g>
      )}

      {showItems && (
        <text x={left} y={barY - 9} fontSize={11} className="fill-muted-foreground pointer-events-none">
          {items} {items === 1 ? 'item' : 'items'}
        </text>
      )}

      {showBar && (
        <g className="pointer-events-none">
          <rect x={left} y={barY} width={room} height={4} rx={2} ry={2} className={tone.track} />
          {progress > 0 && <rect x={left} y={barY} width={Math.max(4, (room * Math.min(100, progress)) / 100)} height={4} rx={2} ry={2} className={tone.solid} />}
        </g>
      )}

      {showBlockedPill && (
        <g className="pointer-events-none">
          <rect x={x + GAP + w - PADDING - pillWidth} y={top + 11} width={pillWidth} height={20} rx={10} ry={10} className="fill-destructive/10" />
          <text x={x + GAP + w - PADDING - pillWidth / 2} y={top + 25} textAnchor="middle" fontSize={11} fontWeight={600} className="fill-destructive">
            {blockedText}
          </text>
        </g>
      )}

      {showBlockedMark && (
        <g className="pointer-events-none">
          <circle cx={x + GAP + w - PADDING - 6} cy={top + 21} r={7} className="fill-destructive/10" />
          <text x={x + GAP + w - PADDING - 6} y={top + 25} textAnchor="middle" fontSize={11} fontWeight={700} className="fill-destructive">
            !
          </text>
        </g>
      )}
    </g>
  );
}
